import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, Loader2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

export const DEFAULT_MATOMO_CONFIG = {
  enabled: false,
  base_url: '',
  site_id: '',
};

export function mergeMatomoFromApi(data) {
  const raw = data || {};
  return {
    enabled: Boolean(raw.enabled),
    base_url: String(raw.base_url || '').trim(),
    site_id: String(raw.site_id || '').trim(),
  };
}

const AdminMatomoTab = ({ apiUrl, token, config, onChange, onSaved }) => {
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    onChange({ ...config, [key]: value });
  };

  const handleSave = async () => {
    const payload = mergeMatomoFromApi(config);
    if (payload.enabled && (!payload.base_url || !payload.site_id)) {
      toast.error('URL Matomo et ID du site sont requis lorsque Matomo est activé.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${apiUrl}/api/admin/matomo-config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(payload);
      onSaved?.(payload);
      toast.success('Configuration Matomo enregistrée');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Échec de l’enregistrement Matomo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-[#1A2332] border-[#2D3748]">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#FF6B35]" />
          Matomo (statistiques)
        </CardTitle>
        <CardDescription className="text-gray-400">
          Instance Matomo pour les statistiques de visite (respect du consentement cookies analytics).
          Configuration stockée en base — pas dans les variables d’environnement du build.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setField('enabled', e.target.checked)}
            className="rounded border-[#2D3748] bg-[#0B1120] text-blue-600 focus:ring-blue-500"
          />
          <span className="text-white font-medium">Activer Matomo sur le site public</span>
        </label>

        <div className={`space-y-4 ${config.enabled ? '' : 'opacity-60'}`}>
          <div className="space-y-2">
            <Label htmlFor="matomo-base-url" className="text-white">
              URL de l’instance Matomo
            </Label>
            <Input
              id="matomo-base-url"
              type="url"
              placeholder="https://votre-compte.matomo.cloud"
              value={config.base_url}
              onChange={(e) => setField('base_url', e.target.value)}
              disabled={!config.enabled}
              className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500 disabled:opacity-50"
            />
            <p className="text-xs text-gray-400">
              URL racine sans slash final (ex. cloud Matomo ou serveur self-hosted).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="matomo-site-id" className="text-white">
              ID du site (siteId)
            </Label>
            <Input
              id="matomo-site-id"
              type="text"
              inputMode="numeric"
              placeholder="1"
              value={config.site_id}
              onChange={(e) => setField('site_id', e.target.value)}
              disabled={!config.enabled}
              className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500 disabled:opacity-50"
            />
            <p className="text-xs text-gray-400">
              Numéro du site dans Matomo (Administration → Sites).
            </p>
          </div>
        </div>

        {config.enabled ? (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm text-gray-300 space-y-1">
            <p>
              <strong className="text-blue-300">Dev / prod :</strong> chaque environnement a sa propre base Mongo ;
              configurez une URL et un siteId distincts si besoin.
            </p>
            <p>
              Le script ne se charge qu’après acceptation des cookies « analytics » par le visiteur.
            </p>
          </div>
        ) : null}

        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement…
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer Matomo
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminMatomoTab;
